import { useEffect, useState } from 'react';
import { useAppSelector } from '../../../Redux/hooks';
import { useTranslation } from '../../../i18n/useTranslation';
import type { Language } from '../../../Models/enums';
import type { TranslationKey } from '../../../i18n/he';
import { ApiError } from '../../../Models/ApiModel';
import type { BusinessSettingsModel, ReceiptModel, ReceiptPaymentModel } from '../../../Models/ReceiptModel';
import { ReceiptService } from '../../../Services/ReceiptService';
import { formatDate } from '../../../Utils/DateUtil';
import { partyDisplayName } from '../../../Utils/DisplayNameUtil';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Modal } from '../../SharedArea/Modal/Modal';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import './ReceiptDocument.css';

/**
 * The receipt itself.
 *
 * Mobile first, because almost every one of these is opened on a phone. A4 is
 * what printing adds on top, not the other way round: the print rules sit in
 * a media query at the end of the stylesheet and the phone layout is the
 * design.
 *
 * What is on it is not a design decision. הוראות מס הכנסה (ניהול פנקסי
 * חשבונות) and תקנות מס ערך מוסף (ניהול פנקסים) name the parts, and all of
 * them are here: the document's own title, the one original and the copies
 * that follow it, the fact that a computer produced it, the business with its
 * number and address, who paid, what for, how much, how the money arrived,
 * and for a business that charges VAT, the split. Removing any of these makes
 * the document one a tutor cannot hand over.
 */
export function ReceiptDocument({
  receipt,
  onClose,
  onChanged
}: { receipt: ReceiptModel; onClose: () => void; onChanged: () => void }) {
  const { tr, language } = useTranslation();
  const [current, setCurrent] = useState(receipt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const business = current.businessSnapshot;

  // In English the parties are spelled from today's roster and settings. The
  // snapshot is what was issued and is never rewritten; this only changes what
  // an English reader is shown, and falls back to the snapshot field by field.
  const students = useAppSelector(state => state.students.items);
  const [spelling, setSpelling] = useState<BusinessSettingsModel | null>(null);
  useEffect(() => {
    if (language !== 'en') return;
    let cancelled = false;
    ReceiptService.getSettings().then(loaded => { if (!cancelled) setSpelling(loaded); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [language]);
  const english = language === 'en' ? spelling : null;
  const businessName = english?.businessNameEn?.trim() || business?.businessName;
  const addressLine = english?.addressEn?.trim() || businessAddress(business);
  const signatureName = english?.signatureNameEn?.trim() || business?.signatureName;
  const footerText = english?.footerTextEn?.trim() || business?.footerText;
  const customerName = partyDisplayName(current.customerName ?? current.student ?? '', students, language);
  const isDraft = current.status === 'draft';
  const isCancelled = current.status === 'cancelled';
  const chargesVat = (current.vatRate ?? 0) > 0;

  // The first print is the one מקור. Reading it before printing and stamping
  // it afterwards is what makes the sequence honest: this render is the
  // original, and the next one knows it is not.
  const isCopy = !isDraft && current.originalPrintedAt !== null;

  async function issue() {
    setBusy(true);
    setError(null);
    try {
      const issued = await ReceiptService.issue(current.id);
      setCurrent(issued);
      onChanged();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  function print() {
    window.print();
    // Reported after the dialog opens, so the page that went to the printer is
    // the one rendered above, and the receipt comes back marked as copied for
    // every print after this one.
    void ReceiptService.recordEvent(current.id, 'printed')
      .then(updated => {
        setCurrent(updated);
        onChanged();
      })
      .catch(() => {});
  }

  return (
    <Modal
      title={tr(isDraft ? 'receiptDraft' : docTypeKey(current.docType))}
      {...(current.receiptNumber ? { subtitle: current.receiptNumber } : {})}
      onClose={onClose}
      footer={
        <>
          {!isDraft && (
            <button type="button" className="ghost-button" onClick={print}>{tr('print')}</button>
          )}
          {isDraft && (
            <button type="button" className="primary-button" onClick={() => void issue()} disabled={busy}>
              {busy ? <Spinner inline /> : tr('issueReceipt')}
            </button>
          )}
        </>
      }
    >
      <article className="receipt-doc" data-status={current.status} data-cancelled={isCancelled || undefined}>
        {/* The document names itself, and says whether this sheet is the one
            original or a copy of it. Both are required on a computerised
            document and neither is inferable from the rest of the page. */}
        <div className="receipt-doc-title-row">
          <h2 className="receipt-doc-title">{tr(isDraft ? 'receiptDraft' : docTypeKey(current.docType))}</h2>
          {!isDraft && (
            <span className="receipt-doc-origin" data-copy={isCopy || undefined}>
              {tr(isCopy ? 'docCopy' : 'docOriginal')}
            </span>
          )}
        </div>

        <header className="receipt-doc-header">
          {business?.logo && <img className="receipt-doc-logo" src={business.logo} alt="" />}
          <div className="receipt-doc-business-block">
            <p className="receipt-doc-business">{businessName ?? tr('appName')}</p>
            {business?.registrationNumber && (
              <p className="receipt-doc-meta">
                {tr(registrationLabelKey(business.businessType))}{' '}
                <bdi>{business.registrationNumber}</bdi>
              </p>
            )}
            {addressLine && (
              <p className="receipt-doc-meta">{addressLine}</p>
            )}
            {business?.phone && <p className="receipt-doc-meta"><bdi>{business.phone}</bdi></p>}
            {business?.email && <p className="receipt-doc-meta"><bdi>{business.email}</bdi></p>}
          </div>
        </header>

        <dl className="receipt-doc-facts">
          <div>
            <dt>{tr('receiptFor')}</dt>
            <dd>
              {customerName}
              {current.customerTaxId && <><br /><bdi>{current.customerTaxId}</bdi></>}
              {current.customerAddress && <><br />{current.customerAddress}</>}
            </dd>
          </div>
          {current.receiptNumber && (
            <div>
              <dt>{tr('receiptNumber')}</dt>
              <dd><bdi>{current.receiptNumber}</bdi></dd>
            </div>
          )}
          {current.issueDate && (
            <div>
              <dt>{tr('issueDate')}</dt>
              <dd><bdi>{formatDate(current.issueDate, language)}</bdi></dd>
            </div>
          )}
          {/* Printed whenever it differs from the issue date, which is the
              case this field exists for: the money came in on the 3rd, the
              document was written on the 19th, and the receipt says so. */}
          {current.paymentDate && current.paymentDate !== current.issueDate && (
            <div>
              <dt>{tr('paymentDate')}</dt>
              <dd><bdi>{formatDate(current.paymentDate, language)}</bdi></dd>
            </div>
          )}
        </dl>

        {current.items && current.items.length > 0 && (
          <ul className="receipt-doc-lines">
            {current.items.map(item => (
              <li key={item.id}>
                <span>
                  {item.description ?? (item.date ? <bdi>{formatDate(item.date, language)}</bdi> : null)}
                  {item.description && item.date && (
                    <> <bdi className="receipt-doc-line-date">{formatDate(item.date, language)}</bdi></>
                  )}
                </span>
                <MoneyText amount={item.amount} />
              </li>
            ))}
          </ul>
        )}

        {/* A business that charges VAT has to show the split. One that does
            not shows a plain total: the עוסק פטור label beside the number in
            the header is what says there is no VAT, and spelling it out again
            in a sentence teaches the customer a tax lesson they did not ask
            for. */}
        {chargesVat ? (
          <dl className="receipt-doc-totals">
            <div>
              <dt>{tr('receiptNetAmount')}</dt>
              <dd><MoneyText agorot={current.netAgorot} /></dd>
            </div>
            <div>
              <dt>{tr('receiptVatLine', (current.vatRate ?? 0) / 100)}</dt>
              <dd><MoneyText agorot={current.vatAgorot} /></dd>
            </div>
            <div className="receipt-doc-totals-final">
              <dt>{tr('receiptGrossAmount')}</dt>
              <dd><MoneyText agorot={current.grossAgorot ?? Number(current.amount) * 100} /></dd>
            </div>
          </dl>
        ) : (
          <p className="receipt-doc-total">
            <span>{tr('total')}</span>
            <MoneyText amount={current.amount} />
          </p>
        )}

        {current.payments && current.payments.length > 0 && (
          <section className="receipt-doc-payments">
            <h3>{tr('receiptPaymentDetails')}</h3>
            <ul>
              {current.payments.map((payment, index) => (
                <li key={payment.id ?? index}>
                  <span>{paymentLine(payment, tr, language)}</span>
                  <MoneyText amount={payment.amount} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {isCancelled && (
          <p className="receipt-doc-cancelled">
            {tr('receiptCancelled')} <bdi>{current.cancellationReason}</bdi>
          </p>
        )}

        {signatureName && (
          <p className="receipt-doc-signature">
            {tr('receiptIssuedBy')} <bdi>{signatureName}</bdi>
          </p>
        )}

        {footerText && <p className="receipt-doc-footer">{footerText}</p>}

        {/* The regulations require a computer-produced document to say so on
            its face, alongside the registry number of the software that
            produced it. */}
        {!isDraft && (
          <p className="receipt-doc-legal">
            <span>{tr('docComputerGenerated')}</span>
            {business?.softwareRegistryNumber && (
              <> · <bdi>{tr('docSoftwareRegistry', business.softwareRegistryNumber)}</bdi></>
            )}
          </p>
        )}
      </article>

      {error && <p className="receipt-doc-error" role="alert">{tr(errorKey(error))}</p>}
    </Modal>
  );
}

function docTypeKey(docType: string): TranslationKey {
  return docType === 'tax_invoice_receipt' ? 'docTypeTaxInvoiceReceipt' : 'docTypeReceipt';
}

/**
 * The number on a document is meaningless unlabelled: the same nine digits are
 * an עוסק number on one business and a ח.פ. on another, and the reader has to
 * be told which.
 */
function registrationLabelKey(businessType: string | undefined): TranslationKey {
  if (businessType === 'osek_murshe') return 'docVatRegisteredLabel';
  if (businessType === 'company') return 'docCompanyLabel';
  if (businessType === 'amuta') return 'docAmutaLabel';
  return 'docVatExemptLabel';
}

/** The parts if they were filled in separately, otherwise the free-text line. */
function businessAddress(business: BusinessSettingsModel | null): string | null {
  if (!business) return null;
  const parts = [
    [business.addressStreet, business.addressHouse].filter(Boolean).join(' '),
    business.addressCity,
    business.addressZip
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : business.address;
}

const METHOD_KEYS: Record<string, TranslationKey> = {
  cash: 'methodCash',
  bank_transfer: 'methodBankTransfer',
  credit_card: 'methodCreditCard',
  bit: 'methodBit',
  paybox: 'methodPaybox',
  check: 'methodCheck',
  other: 'methodOther'
};

/**
 * One tender, spelled out the way the regulations require: a cheque names its
 * bank, branch, account, number and due date, a card names its issuer and the
 * last four digits, and anything else names its reference if it has one.
 */
function paymentLine(
  payment: ReceiptPaymentModel,
  tr: (key: TranslationKey, ...args: (string | number)[]) => string,
  language: Language
): string {
  const label = tr(METHOD_KEYS[payment.method] ?? 'methodOther');
  if (payment.method === 'check') {
    const details = tr(
      'chequeLine',
      payment.bankNumber ?? '', payment.branchNumber ?? '',
      payment.accountNumber ?? '', payment.chequeNumber ?? ''
    );
    const due = payment.dueDate ? ` ${tr('chequeDueDate', formatDate(payment.dueDate, language))}` : '';
    return `${label}: ${details}${due}`;
  }
  if (payment.method === 'credit_card') {
    const brand = payment.cardBrand ? ` ${payment.cardBrand}` : '';
    const last4 = payment.cardLast4 ? ` ${tr('cardEnding', payment.cardLast4)}` : '';
    return `${label}:${brand}${last4}`.trim();
  }
  return payment.reference ? `${label}: ${payment.reference}` : label;
}

/** Specific codes get specific copy; anything else falls back to the generic. */
const ERROR_KEYS: Record<string, TranslationKey> = {
  REGISTRATION_NUMBER_REQUIRED: 'errorRegistrationNumberRequired',
  PAYMENTS_DO_NOT_BALANCE: 'errorPaymentsDoNotBalance',
  PAYMENT_DETAILS_REQUIRED: 'errorPaymentDetailsRequired',
  INVALID_CUSTOMER_TAX_ID: 'errorInvalidCustomerTaxId'
};

function errorKey(code: string): TranslationKey {
  return ERROR_KEYS[code] ?? 'errorGeneric';
}
